<!--
  - Copyright (c) 2003-2019 GameDuell GmbH, All Rights Reserved
  - This document is strictly confidential and sole property of GameDuell GmbH, Berlin, Germany
  -->

<template>
  <div class="tournaments-registration-panel" data-qe-id="registration-panel">
    <div class="registration-info">
      <div class="tournament-details">
        <div class="tournament-details--image" :class="tournamentUi.metaData.image"></div>
        <div class="t-text">
          <h1>{{ tournamentUi.name }}</h1>
          <c-tournament-info
            :tournament-ui="tournamentUi"
            class="tournament-info-box"
            gameName
            totalWin
            addedPrize
            entryFee
            restartFee
            competitionDateLobbyTime
            registrationDate
          />
        </div>
      </div>
      <c-tech-hint
        class="c-tech-button"
        v-if="!registrationIsOver"
        :gametype="tournamentUi.gametype"
        :techs="tournamentUi.metaData.gameTechs"
      >
        <template v-slot:default="status">
          <t-tooltip
            :disabled="registrationPossible || status.block"
            :text="
              $t('tViews.registrationInfo.registrationDidNotStart_info', {
                registrationStart,
              })
            "
          >
            <t-button
              yellow
              l
              :inactive="status.block"
              :disabled="!(registrationPossible || status.block)"
              @click="register(status.block)"
              data-qe-id="register-button"
            >
              {{ $t('tViews.registrationInfo.button') }}
            </t-button>
          </t-tooltip>
        </template>
      </c-tech-hint>
      <p v-else class="t-inlay-tip t-tc">
        {{ $t('tViews.registrationInfo.registrationTimeOver') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import CTournamentInfo from '@/components/CTournamentInfo.vue'
import { TournamentStatus } from '@/model/TournamentStatus'
import { TournamentUi } from '@/model/Tournament'
import { DateFormat } from '@gd/i18n'

const props = defineProps<{ tournamentUi: TournamentUi }>()
const emit = defineEmits(['register'])
const { d } = useI18n()

function register(blocked: boolean) {
  if (blocked) {
    return
  }
  if (registrationPossible.value) emit('register')
}

const registrationStart = computed(() => {
  return d(new Date(props.tournamentUi.dates.registrationStart), DateFormat.DDMMYYY)
})
const registrationPossible = computed(() => {
  return props.tournamentUi.isRegistrationPossible
})
/**
 * the case that the registration time ended and a tournament is not
 * in running mode is not handled ( because it should not happen )
 */
const registrationIsOver = computed(() => {
  return !(registrationPossible.value || props.tournamentUi.tournamentStatus === TournamentStatus.UPCOMING)
})
</script>

<style lang="less">
@import '~@npm/@gd/common-styles/dist/card';
@import '~@npm/@gd/common-styles/dist/color-vars';
@import '../../styles/vars';
.tournaments-registration-panel {
  .registration-info {
    /* padding: 0 1.4.75rem; that's the padding that we defined as generic but it don't fits for such a big box, which functions as a teaser */
    padding: @biggerspacing @biggerspacing 0 @biggerspacing;
    margin-bottom: 6.25em;
    @media screen and(max-width: 800px) {
      padding: @biggerspacing @smallerspacing 0 @smallerspacing;
    }
    .tournament-details {
      display: flex;
      @media screen and(max-width: 800px) {
        display: block;
      }
      &--image {
        width: 22.5em;
        height: 15.7em;
        background-size: contain;
        background-repeat: no-repeat;
        margin-right: @biggerspacing;
        flex-grow: 0;
        @media screen and(max-width: 800px) {
          margin: 0 auto @smallerspacing auto;
        }
      }
      .t-text {
        text-align: left;
        flex-grow: 1;
      }
      // default images (zoneId)
      .skat {
        background-image: url(../../assets/teasers/skat_default_teaser.png);
      }
      .romme {
        background-image: url(../../assets/teasers/romme_default_teaser.png);
      }
      // special tournaments ( names are defined by backend )
      .skatAnniversary {
        background-image: url(../../assets/teasers/skatAnniversary_teaser.png);
      }
      .rommeAnniversary {
        background-image: url(../../assets/teasers/rommeAnniversary_teaser.png);
      }
      .skatNewYear {
        background-image: url(../../assets/teasers/skatNewYear_tournament.png);
      }
      .speedSkat {
        background-image: url(../../assets/teasers/speedskat_teaser.png);
      }
    }
    .colorScheme.midGreen & {
      .card(@colorScheme--midGreen);
    }
    .colorScheme.red & {
      .card(@colorScheme--red);
    }
    h1 {
      margin-top: 0.21em;
      @media screen and(max-width: 800px) {
        text-align: center;
      }
    }
    h3 {
      @media screen and(max-width: 800px) {
        text-align: center;
      }
    }
    .tournament-info-box {
      font-size: 1.25em;
      line-height: 1.25em;
      .layout {
        padding: 0;
      }
    }
    .registration-time-over {
      color: red;
    }
    .c-tech-button {
      position: relative;
      bottom: -1.9em;
      z-index: 1;
    }
    .t-inlay-tip {
      display: inline-block;
      margin: 1.5em 1em;
    }
  }
}
</style>
